import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ExcelDocumentRowStatusDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/excel-document-row-status-dto';
import { ExcelDocumentTypeEnum } from 'src/app/api/models/OK/Egis/Service/Models/DTO/Enums/excel-document-type-enum';
import { HttpClient, HttpRequest, HttpResponse } from '@angular/common/http';
import { ExcelService } from 'src/app/api/services/excel.service';
import { filter, finalize, map, takeUntil } from 'rxjs/operators';
import { StrictHttpResponse } from 'src/app/api/strict-http-response';
import { ExcelDocumentUploadResponseDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/excel-document-upload-response-dto';
import { ExcelDocumentRowStateEnum } from 'src/app/api/models/OK/Egis/Service/Models/DTO/Enums/excel-document-row-state-enum';
import { NotificationType } from 'src/app/notifications/models/notification-models';
import { LoaderService } from 'src/app/services/loader.service';
import { NotificationService } from 'src/app/notifications/services/notification.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { ExcelDocumentHandlerTypeEnum } from 'src/app/api/models/OK/Egis/Service/Models/DTO/Enums/excel-document-handler-type-enum';
import { SignalrService } from 'src/app/services/signalr.service';
import { MatDialog } from '@angular/material/dialog';
import { ExcelDownloadDialogComponent } from '../excel-download-dialog/excel-download-dialog.component';
import { ExcelDownloadDialogModel } from '../excel-download-dialog/excel-download-dialog.model';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-excel',
  templateUrl: './excel.component.html',
  styleUrls: ['./excel.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ExcelComponent implements OnInit {
  @Input() Filter: string;
  @Input() DocumentType: ExcelDocumentTypeEnum;
  @Input() HandlerType: ExcelDocumentHandlerTypeEnum;
  @Input() ShowDownloadCta: string;

  selectedFile: File;
  uploadresult: Array<ExcelDocumentRowStatusDto>;
  uploaderrors: Array<ExcelDocumentRowStatusDto>;
  uploadwarnings: Array<ExcelDocumentRowStatusDto>;
  uploaderrorssummary: { [key: string]: number } = {};
  uploadwarningssummary: { [key: string]: number } = {};
  uploadsuccesssummary: { [key: string]: number } = {};

  constructor(
    private loader: LoaderService,
    private excelService: ExcelService,
    private notificationService: NotificationService,
    private http: HttpClient,
    private errorHandler: ErrorHandlerService,
    private signalR: SignalrService,
    private matDialog: MatDialog,
  ) {}

  unsubscribe$ = new Subject<void>();

  ngOnInit(): void {
    this.signalR.excelUploaded$.pipe(takeUntil(this.unsubscribe$)).subscribe((response: ExcelDocumentUploadResponseDto) => {
      if (response.requestId == this.uploadInprogress && response.rows?.length) {
        if (response.validateOnly) {
          this.uploadresult = response.rows;
        } else {
          const updated = response.rows.filter((p) => p.state == ExcelDocumentRowStateEnum.Ok).length;
          this.notificationService.success(`${updated} aftaler opdateret`);
        }
        this.uploadInprogress = '';
        this.setuploaddata();
      }
    });

    this.signalR.excelUploadFailed$.pipe(takeUntil(this.unsubscribe$)).subscribe((message: string) => {
      this.errorHandler.ShowGenericError();
      this.uploadInprogress = '';
    });
  }

  chooseFile(files: FileList) {
    this.setFile(files[0]);
  }

  setFile(file: File) {
    this.uploaderrors = [];
    this.uploaderrorssummary = {};

    this.uploadwarnings = [];
    this.uploadwarningssummary = {};

    this.selectedFile = file;

    this.sendExcel(true);
  }

  sendExcel(validateOnly: boolean = false) {
    this.loader.show();
    this.uploadresult = [];
    const formData = new FormData();
    formData.append('Document', this.selectedFile);
    formData.append('id', this.Filter);
    formData.append('documentType', this.DocumentType);
    formData.append('validateOnly', validateOnly.toString());
    const req = new HttpRequest('POST', `${this.excelService.rootUrl}${ExcelService.UploadDocumentPath}`, formData);
    this.http
      .request(req)
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ExcelDocumentUploadResponseDto>;
        }),
        finalize(() => {
          this.loader.hide();
          if (!validateOnly) {
            this.selectedFile = null;
            this.displayUpload = false;
          }
        }),
      )
      .subscribe({
        next: (r) => {
          this.uploadInprogress = r.body.requestId;
        },
        error: (err) => this.errorHandler.HandleError(err),
      });
  }

  setuploaddata() {
    this.uploaderrors = [];
    this.uploadwarnings = [];
    this.uploaderrorssummary = {};
    this.uploadwarningssummary = {};
    this.uploadsuccesssummary = {};
    if (this.uploadresult) {
      this.uploaderrors = this.uploadresult.filter((p) => p.state == ExcelDocumentRowStateEnum.Error);
      this.uploaderrors.forEach((w) => {
        if (w.rowNumber === 0) {
          // fatal error
          this.uploaderrorssummary[w.message] = 0;
          return;
        }
        if (this.uploaderrorssummary[w.message] === undefined) this.uploaderrorssummary[w.message] = 1;
        else this.uploaderrorssummary[w.message]++;
      });

      this.uploadwarnings = this.uploadresult.filter((p) => p.state == ExcelDocumentRowStateEnum.Warning);
      this.uploadwarnings.forEach((w) => {
        if (!this.uploadwarningssummary[w.message]) this.uploadwarningssummary[w.message] = 1;
        else this.uploadwarningssummary[w.message]++;
      });

      this.uploadresult
        .filter((p) => p.state == ExcelDocumentRowStateEnum.Ok)
        .forEach((w) => {
          if (!this.uploadsuccesssummary[w.message]) this.uploadsuccesssummary[w.message] = 1;
          else this.uploadsuccesssummary[w.message]++;
        });
    }
  }

  create() {
    const data: ExcelDownloadDialogModel = {
      filter: this.Filter,
      documentType: this.DocumentType,
      handlerType: this.HandlerType,
    };
    this.matDialog.open(ExcelDownloadDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: data,
    });
  }

  uploadsummarykeys(item: any): { key: string; value: number }[] {
    var result: { key: string; value: number }[] = [];
    for (const key in item) {
      result.push({ key, value: item[key] });
    }
    return result;
  }

  uploadresultformessage(key): Array<ExcelDocumentRowStatusDto> {
    return this.uploadresult.filter((p) => p.message == key && p.id);
  }

  displayUpload: boolean = false;
  displayFileDrag: boolean = false;
  uploadInprogress: string = '';

  toggleUpload() {
    this.displayUpload = !this.displayUpload;
  }

  handleDrop(event: DragEvent) {
    event.preventDefault();
    this.displayFileDrag = false;
    if (event.dataTransfer.items) {
      // If dropped items aren't files, reject them
      if (event.dataTransfer.items[0].kind === 'file') {
        var file = event.dataTransfer.items[0].getAsFile();
        this.setFile(file);
      }
    } else {
      // Use DataTransfer interface to access the file(s)
      if (event.dataTransfer.files.length) {
        this.setFile(event.dataTransfer.files[0]);
      }
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.displayFileDrag = event.dataTransfer.items[0].kind === 'file' || !!event.dataTransfer.files.length;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.displayFileDrag = false;
  }
}
